import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, ShoppingBag, Heart, UserRound, Menu, X, ArrowRight, Leaf, Star, Plus, Minus, Trash2, ChevronRight, MessageCircle, MapPin, Truck, ShieldCheck, Sprout, LayoutDashboard, Package, LogOut, Phone, Mail, Save, CheckCircle2, Upload, Pencil, Image as ImageIcon } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
const img = {
  hero: "https://images.unsplash.com/photo-1617173944883-6ffbd35d584d?auto=format&fit=crop&w=1800&q=85",
  plant: "https://images.unsplash.com/photo-1525498128493-380d1990a112?auto=format&fit=crop&w=900&q=85",
  pot: "https://images.unsplash.com/photo-1679078597160-02b6240b8d4b?auto=format&fit=crop&w=900&q=85",
  soil: "https://images.unsplash.com/photo-1557296691-edb10ad8da28?auto=format&fit=crop&w=900&q=85",
  tools: "https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?auto=format&fit=crop&w=900&q=85",
};
const cats = ["Indoor Plants", "Outdoor Plants", "Flowering Plants", "Air Purifying Plants", "Money Plants", "Succulents & Cactus", "Pots & Planters", "Soil & Potting Mix", "Fertilizers", "Gardening Tools"];
const shopCategories = ["Indoor Plants", "Outdoor Plants", "Pots & Planters", "Soil & Potting Mix", "Fertilizers", "Gardening Products", "Plant Care Services"];
function money(n) { return `₹${Number(n).toLocaleString("en-IN")}` }
function digits(s) { return String(s || "").replace(/\D/g, "") }
function resolveImg(u) { return u && typeof u === "string" && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u; }
function waLink(number, text) {
  const num = digits(number);
  const t = encodeURIComponent(text || "Hello ROPAN");
  return num ? `https://wa.me/${num}?text=${t}` : `https://wa.me/?text=${t}`;
}

function useStore() {
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("ropan-cart") || "[]"));
  const [wish, setWish] = useState(() => JSON.parse(localStorage.getItem("ropan-wish") || "[]"));
  useEffect(() => localStorage.setItem("ropan-cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("ropan-wish", JSON.stringify(wish)), [wish]);
  return {
    cart, wish,
    add: (p) => setCart(c => { let x = c.find(i => i.id === p.id); const max = p.stock ?? 99; return x ? c.map(i => i.id === p.id ? { ...i, quantity: Math.min(max, i.quantity + 1) } : i) : [...c, { ...p, quantity: 1 }] }),
    remove: (id) => setCart(c => c.filter(i => i.id !== id)),
    qty: (id, n) => setCart(c => c.map(i => i.id === id ? { ...i, quantity: Math.min(i.stock ?? 99, Math.max(1, i.quantity + n)) } : i)),
    clear: () => setCart([]),
    toggle: (p) => setWish(w => w.find(i => i.id === p.id) ? w.filter(i => i.id !== p.id) : [...w, p])
  }
}

function useSettings() {
  const [settings, setSettings] = useState({ whatsapp: "918356098426", phone: "+91 83560 98426", email: "hello@ropan.in", address: "Mumbai, Maharashtra, India", business_hours: "Mon–Sat, 9am–6pm", instagram: "", facebook: "", delivery_charge: 79, free_delivery_minimum: 999 });
  const reload = () => api.get("/settings").then(r => setSettings(s => ({ ...s, ...r.data }))).catch(() => {});
  useEffect(() => { reload(); }, []);
  return { settings, reload };
}

function Header({ store }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const go = e => { e.preventDefault(); nav(`/shop?search=${encodeURIComponent(q)}`); setOpen(false); };
  return <>
    <div className="topline">Free delivery on orders above ₹999 <span>•</span> Thoughtful plants, delivered with care</div>
    <header className="header">
      <Link to="/" className="logo" data-testid="header-logo" onClick={() => setOpen(false)}><span>ROPAN</span><small>GROW WITH JOY</small></Link>
      <nav className={open ? "nav open" : "nav"}>
        {["Plants", "Pots & Planters", "Soil", "Fertilizers", "Gardening", "Plant Care", "About", "Contact"].map(x =>
          <Link data-testid={`nav-${x.toLowerCase().replaceAll(" ", "-")}`} key={x} to={x === "Plant Care" ? "/plant-care" : x === "About" ? "/about" : x === "Contact" ? "/contact" : `/shop?category=${encodeURIComponent(x === "Gardening" ? "Gardening Products" : x === "Plants" ? "Indoor Plants" : x)}`} onClick={() => setOpen(false)}>{x}</Link>
        )}
      </nav>
      <div className="header-actions">
        <form onSubmit={go} className="search"><Search size={17} /><input data-testid="header-search-input" placeholder="Search plants, pots..." value={q} onChange={e => setQ(e.target.value)} /></form>
        <Link to="/account" className="icon-btn" data-testid="account-link" onClick={() => setOpen(false)}><UserRound size={20} /></Link>
        <Link to="/wishlist" className="icon-btn" data-testid="wishlist-link" onClick={() => setOpen(false)}><Heart size={20} /><b>{store.wish.length}</b></Link>
        <Link to="/cart" className="icon-btn" data-testid="cart-link" onClick={() => setOpen(false)}><ShoppingBag size={20} /><b>{store.cart.reduce((a, i) => a + i.quantity, 0)}</b></Link>
        <button className="menu-btn" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
      </div>
    </header>
  </>
}

function Footer({ settings }) {
  return <footer>
    <div className="footer-main">
      <div>
        <Link to="/" className="logo light">ROPAN<small>GROW WITH JOY</small></Link>
        <p>Thoughtfully grown plants and garden essentials for your everyday spaces.</p>
      </div>
      <div>
        <h4>Shop</h4>
        <Link to="/shop?category=Indoor%20Plants">Indoor plants</Link>
        <Link to="/shop?category=Pots%20%26%20Planters">Pots & planters</Link>
        <Link to="/shop?category=Fertilizers">Fertilizers</Link>
      </div>
      <div>
        <h4>Care</h4>
        <Link to="/plant-care">Plant care services</Link>
        <Link to="/about">Our story</Link>
        <Link to="/contact">Contact ROPAN</Link>
      </div>
      <div>
        <h4>Let's grow together</h4>
        <p><a href={`mailto:${settings.email}`} data-testid="footer-email">{settings.email}</a><br />{settings.address}</p>
        <a href={waLink(settings.whatsapp, "Hello ROPAN")} target="_blank" rel="noreferrer" data-testid="footer-whatsapp">Chat on WhatsApp <ArrowRight size={15} /></a>
      </div>
    </div>
    <div className="footer-bottom">© 2025 ROPAN. All Rights Reserved. <span>Privacy · Terms · Shipping</span></div>
  </footer>
}

function ProductCard({ p, store }) {
  const nav = useNavigate();
  const available = (p.stock ?? 0) > 0;
  return <article className="product-card" data-testid={`product-card-${p.id}`}>
    <div className="product-image">
      <img src={resolveImg(p.image)} alt={p.name} />
      <button className={store.wish.some(x => x.id === p.id) ? "wish active" : "wish"} data-testid={`wishlist-button-${p.id}`} onClick={() => store.toggle(p)}><Heart size={18} fill="currentColor" /></button>
      {p.discount > 0 && <span className="discount">-{p.discount}%</span>}
    </div>
    <div className="product-info">
      <div className="stars"><Star size={13} fill="currentColor" /> {p.rating} <span>({p.reviews})</span></div>
      <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`} className="product-name">{p.name}</Link>
      <p>{p.description}</p>
      <div className="price"><strong>{money(p.price)}</strong><del>{money(p.compare_price)}</del></div>
      <div className="stock" data-testid={`availability-${p.id}`}><span /> {available ? `In stock · ${p.stock} available` : `Currently unavailable`}</div>
      <div className="card-actions">
        <button className="btn primary small" disabled={!available} data-testid={`add-cart-${p.id}`} onClick={() => store.add(p)}>{available ? "Add to cart" : "Unavailable"}</button>
        <button className="quick-buy" disabled={!available} data-testid={`buy-now-${p.id}`} onClick={() => { store.add(p); nav("/checkout") }}>Buy now <ArrowRight size={14} /></button>
      </div>
    </div>
  </article>
}

function Home({ store }) {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(r => setProducts(r.data.products)) }, []);
  return <>
    <section className="hero">
      <img src={img.hero} alt="Lush green houseplant" />
      <div className="hero-copy">
        <div className="eyebrow"><Leaf size={16} /> PLANTS FOR SLOWER LIVING</div>
        <h1>Bring nature<br /><em>home</em> with ROPAN</h1>
        <p>Healthy plants, beautiful pots & everything you need for your garden.</p>
        <div className="hero-buttons">
          <Link to="/shop?category=Indoor%20Plants" className="btn primary" data-testid="hero-shop-plants">Shop plants <ArrowRight size={17} /></Link>
          <Link to="/shop" className="btn ghost" data-testid="hero-shop-gardening">Shop gardening products</Link>
        </div>
      </div>
      <div className="hero-note"><span>01</span><div><b>Find your green</b><small>New arrivals for every corner</small></div></div>
    </section>
    <section className="trust-row">
      <div><Truck /> Careful doorstep delivery</div>
      <div><Sprout /> Plants packed with love</div>
      <div><ShieldCheck /> 7-day plant promise</div>
    </section>
    <section className="section categories">
      <div className="section-head"><div><span className="kicker">CURATED FOR YOU</span><h2>Shop by category</h2></div><Link to="/shop" className="text-link">View all <ArrowRight size={15} /></Link></div>
      <div className="category-grid">
        {cats.map((c, i) => <Link data-testid={`category-${i}`} key={c} to={`/shop?category=${encodeURIComponent(c)}`} className={`category-tile tile-${i}`}>
          <img src={img[i % 4 === 0 ? "plant" : i % 4 === 1 ? "pot" : i % 4 === 2 ? "soil" : "tools"]} alt="" />
          <span>{c}</span><ArrowRight size={17} />
        </Link>)}
      </div>
    </section>
    <section className="section best">
      <div className="section-head"><div><span className="kicker">OUR COMMUNITY FAVOURITES</span><h2>Best sellers</h2></div><Link to="/shop" className="text-link">Shop all plants <ArrowRight size={15} /></Link></div>
      <div className="product-grid">{products.slice(0, 8).map(p => <ProductCard key={p.id} p={p} store={store} />)}</div>
    </section>
    <section className="care-banner">
      <div><span className="kicker">ROPAN PLANT CARE</span><h2>Greenery that<br /><em>grows with you.</em></h2><p>From first potting to full balcony gardens, our plant experts are here to help your spaces thrive.</p><Link to="/plant-care" className="btn light-btn" data-testid="home-plant-care-button">Explore plant care <ArrowRight size={17} /></Link></div>
      <img src={img.plant} alt="Plant care at home" />
    </section>
    <section className="quote"><span>"</span><p>Every plant is a small act of hope.<br />Let's grow something beautiful together.</p><b>— The ROPAN team</b></section>
  </>
}

function Shop({ store }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const params = new URLSearchParams(useLocation().search);
  const category = params.get("category") || "";
  const search = params.get("search") || "";
  useEffect(() => {
    setLoading(true);
    api.get(`/products?category=${encodeURIComponent(category)}&search=${encodeURIComponent(search)}`).then(r => setProducts(r.data.products)).finally(() => setLoading(false));
  }, [category, search]);
  return <main className="shop section">
    <div className="shop-top">
      <div><span className="kicker">ROPAN COLLECTION</span><h1>{category || search ? category || `Results for "${search}"` : "All plants & garden goods"}</h1><p>Thoughtfully chosen essentials for greener everyday living.</p></div>
      <span className="result-count">{products.length} products</span>
    </div>
    <div className="filterbar">
      <div>{shopCategories.map(x => x === "Plant Care Services" ? <Link key={x} to="/plant-care" className="text-link" data-testid="shop-category-plant-care">{x}</Link> : <Link key={x} to={`/shop?category=${encodeURIComponent(x)}`} className={category === x ? "filter-active" : "text-link"} data-testid={`shop-category-${x.toLowerCase().replaceAll(" ", "-")}`}>{x}</Link>)}</div>
      <span>Sort by <select data-testid="sort-select"><option>Featured</option><option>Price: low to high</option></select></span>
    </div>
    {loading ? <div className="loading">Growing your collection...</div> : (products.length ? <div className="product-grid">{products.map(p => <ProductCard key={p.id} p={p} store={store} />)}</div> : <div className="empty compact"><Sprout size={32} /><h2>No products found</h2><Link to="/shop" className="btn primary">See all products</Link></div>)}
  </main>
}

function Product({ store, settings }) {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [wa, setWa] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get(`/products/${id}`).then(r => setP(r.data)) }, [id]);
  if (!p) return <div className="loading">Loading plant...</div>;
  const available = (p.stock ?? 0) > 0;
  return <main className="product-page section">
    <div className="breadcrumbs"><Link to="/shop">Shop</Link><ChevronRight size={14} />{p.name}</div>
    <div className="product-detail">
      <div className="detail-image"><img src={resolveImg(p.image)} alt={p.name} /><span className="image-tag">ROPAN grown</span></div>
      <div className="detail-copy">
        <div className="stars"><Star size={14} fill="currentColor" /> {p.rating} <span>Based on {p.reviews} reviews</span></div>
        <h1>{p.name}</h1>
        <p className="lead">{p.description}</p>
        <div className="detail-price"><strong>{money(p.price)}</strong><del>{money(p.compare_price)}</del><span>-{p.discount}%</span></div>
        <div className="stock" data-testid="product-stock"><span /> {available ? `In stock · ${p.stock} available` : "Currently unavailable"}</div>
        <div className="qty-row">
          <div className="qty">
            <button data-testid="product-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))}><Minus size={15} /></button>
            <b data-testid="product-qty-value">{qty}</b>
            <button data-testid="product-qty-plus" onClick={() => setQty(Math.min(p.stock ?? 99, qty + 1))}><Plus size={15} /></button>
          </div>
          <button className="btn primary grow" disabled={!available} data-testid="product-add-cart" onClick={() => { for (let i = 0; i < qty; i++) store.add(p) }}>Add to cart <ShoppingBag size={17} /></button>
          <button className="icon-square" data-testid="product-wishlist" onClick={() => store.toggle(p)}><Heart size={19} /></button>
        </div>
        <div className="detail-actions">
          <button className="btn dark grow" disabled={!available} data-testid="product-buy-now" onClick={() => { for (let i = 0; i < qty; i++) store.add(p); nav("/checkout") }}>Buy now <ArrowRight size={17} /></button>
          <button className="whatsapp-btn" data-testid="product-whatsapp" onClick={() => setWa(true)}><MessageCircle size={18} /> Order / enquire on WhatsApp</button>
        </div>
        <div className="spec-grid">
          <div><b>Light</b><span>{p.light}</span></div>
          <div><b>Water</b><span>{p.water}</span></div>
          <div><b>Size</b><span>{p.size}</span></div>
          <div><b>Pet friendly</b><span>{p.pet_friendly ? "Yes" : "No"}</span></div>
        </div>
        <details open><summary>Plant care instructions</summary><p>{p.care}</p></details>
        <div className="delivery-note"><Truck size={16} /> Free delivery over ₹999 · Dispatched in 1–2 days · COD available</div>
      </div>
    </div>
    {wa && <div className="modal-backdrop" onClick={() => setWa(false)}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setWa(false)}><X /></button>
        <MessageCircle className="modal-icon" />
        <h2>Let's talk plants</h2>
        <p>We'll help you choose the right {p.name} for your space.</p>
        <a className="btn primary" target="_blank" rel="noreferrer" href={waLink(settings.whatsapp, `Hello ROPAN, I am interested in ${p.name}. Quantity: ${qty}. Price: ${money(p.price)}.`)} data-testid="whatsapp-send-link">Continue to WhatsApp <ArrowRight size={16} /></a>
      </div>
    </div>}
  </main>
}

function Cart({ store, settings }) {
  const subtotal = store.cart.reduce((a, i) => a + i.price * i.quantity, 0);
  const delivery = subtotal >= (settings.free_delivery_minimum || 999) ? 0 : (settings.delivery_charge || 79);
  const total = subtotal + delivery;
  const waText = `Hello ROPAN, I would like to order: ${store.cart.map(i => `${i.name} x ${i.quantity} (${money(i.price * i.quantity)})`).join(", ")}. Total: ${money(total)}`;
  return <main className="section cart-page">
    <div className="shop-top">
      <div><span className="kicker">YOUR GREEN CORNER</span><h1>Your cart</h1></div>
      <span className="result-count">{store.cart.length} items</span>
    </div>
    {!store.cart.length ? <div className="empty"><ShoppingBag size={38} /><h2>Your cart is waiting to grow</h2><p>Add a plant or two and make your space feel alive.</p><Link to="/shop" className="btn primary" data-testid="empty-cart-shop">Explore plants <ArrowRight size={16} /></Link></div> :
      <div className="cart-layout">
        <div className="cart-items">
          {store.cart.map(i => <div className="cart-item" key={i.id}>
            <img src={resolveImg(i.image)} alt="" />
            <div>
              <Link to={`/product/${i.id}`}><b>{i.name}</b></Link>
              <p>{i.description}</p>
              <div className="qty">
                <button data-testid={`cart-minus-${i.id}`} onClick={() => store.qty(i.id, -1)}><Minus size={14} /></button>
                {i.quantity}
                <button data-testid={`cart-plus-${i.id}`} onClick={() => store.qty(i.id, 1)}><Plus size={14} /></button>
              </div>
            </div>
            <strong>{money(i.price * i.quantity)}</strong>
            <button className="remove" data-testid={`cart-remove-${i.id}`} onClick={() => store.remove(i.id)}><Trash2 size={17} /></button>
          </div>)}
          <Link className="text-link" to="/shop"><ArrowRight size={15} className="flip" /> Continue shopping</Link>
        </div>
        <aside className="summary">
          <h3>Order summary</h3>
          <div><span>Subtotal</span><b>{money(subtotal)}</b></div>
          <div><span>Delivery</span><b>{delivery ? money(delivery) : "Free"}</b></div>
          <hr />
          <div className="total"><span>Total</span><b>{money(total)}</b></div>
          <Link to="/checkout" className="btn primary full" data-testid="proceed-checkout">Proceed to checkout <ArrowRight size={16} /></Link>
          <a className="whatsapp-btn" href={waLink(settings.whatsapp, waText)} target="_blank" rel="noreferrer" data-testid="cart-whatsapp-order"><MessageCircle size={17} /> Order this cart on WhatsApp</a>
          <small>Secure checkout · Cash on delivery available</small>
        </aside>
      </div>}
  </main>
}

function Checkout({ store, settings }) {
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "" });
  const nav = useNavigate();
  const subtotal = store.cart.reduce((a, i) => a + i.price * i.quantity, 0);
  const delivery = subtotal >= (settings.free_delivery_minimum || 999) ? 0 : (settings.delivery_charge || 79);
  const total = subtotal + delivery;
  const submit = async e => {
    e.preventDefault();
    setError("");
    if (!store.cart.length) { setError("Your cart is empty. Add a product before placing an order."); return; }
    const token = localStorage.getItem("ropan-token");
    if (!token) { setError("Please sign in to complete your order."); nav("/account"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/orders", { customer: form, items: store.cart, payment_method: "cod" }, { headers: { Authorization: `Bearer ${token}` } });
      setDone(r.data);
      store.clear();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't place your order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  if (done) return <main className="success section">
    <div className="success-mark"><Sprout /></div>
    <span className="kicker">ORDER RECEIVED</span>
    <h1>Thank you, {form.name || "plant parent"}.</h1>
    <p>Your order <b>{done.id}</b> is being prepared with care. We'll message you at {form.mobile || "your mobile"} with delivery updates.</p>
    <div className="success-actions">
      <Link to="/account" className="btn primary" data-testid="order-track-button">Track my order <ArrowRight size={16} /></Link>
      <Link to="/shop" className="btn outline">Keep shopping</Link>
    </div>
  </main>;
  if (!store.cart.length) return <main className="section"><div className="empty"><ShoppingBag size={38} /><h2>Your cart is empty</h2><Link to="/shop" className="btn primary">Explore plants</Link></div></main>;
  return <main className="section checkout">
    <div>
      <span className="kicker">ALMOST HOME</span>
      <h1>Checkout</h1>
      <form onSubmit={submit} data-testid="checkout-form">
        <h3>Delivery details</h3>
        <div className="form-grid">
          {[["name", "Full name"], ["mobile", "Mobile number"], ["email", "Email address"], ["address", "House / street address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, l]) => <label className={k === "address" ? "wide" : ""} key={k}>{l}<input required data-testid={`checkout-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} /></label>)}
        </div>
        <h3>Payment method</h3>
        <label className="payment-option"><input type="radio" checked readOnly /> <span><b>Cash on Delivery</b><small>Pay when your plants arrive</small></span></label>
        <label className="payment-option muted"><input type="radio" disabled /> <span><b>Online payment</b><small>Gateway connection coming soon</small></span></label>
        {error && <div className="form-error" data-testid="checkout-error">{error}</div>}
        <button className="btn primary full" disabled={submitting} data-testid="place-order-button">{submitting ? "Placing your order..." : "Place order"} <ArrowRight size={16} /></button>
      </form>
    </div>
    <aside className="summary">
      <h3>Order summary</h3>
      {store.cart.map(i => <div key={i.id}><span>{i.name} × {i.quantity}</span><b>{money(i.price * i.quantity)}</b></div>)}
      <div className="subtle"><span>Subtotal</span><b>{money(subtotal)}</b></div>
      <div className="subtle"><span>Delivery</span><b>{delivery ? money(delivery) : "Free"}</b></div>
      <hr />
      <div className="total"><span>Total</span><b>{money(total)}</b></div>
      <a className="whatsapp-btn" href={waLink(settings.whatsapp, `Hello ROPAN, I want to place this order via WhatsApp. Total: ${money(total)}. Items: ${store.cart.map(i => `${i.name} x ${i.quantity}`).join(", ")}`)} target="_blank" rel="noreferrer" data-testid="checkout-whatsapp"><MessageCircle size={17} /> Order on WhatsApp instead</a>
    </aside>
  </main>
}

function Account() {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const loadOrders = (t) => api.get("/orders", { headers: { Authorization: `Bearer ${t}` } }).then(x => setOrders(x.data.orders)).catch(() => {});
  const submit = async e => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post(`/auth/${mode}`, form);
      localStorage.setItem("ropan-token", r.data.token);
      setUser(r.data.user);
      if (r.data.user.role === "admin") { nav("/admin"); return; }
      loadOrders(r.data.token);
    } catch (e) {
      setError(e.response?.data?.detail || "Please check your details");
    }
  };
  useEffect(() => {
    const t = localStorage.getItem("ropan-token");
    if (t) api.get("/auth/me", { headers: { Authorization: `Bearer ${t}` } }).then(r => { setUser(r.data); loadOrders(t); }).catch(() => localStorage.removeItem("ropan-token"));
  }, []);
  if (user) return <main className="section account">
    <div className="account-head">
      <div><span className="kicker">WELCOME BACK</span><h1>Hi, {user.name.split(" ")[0]}.</h1><p>{user.email}</p></div>
      <div className="account-head-actions">
        {user.role === "admin" && <Link to="/admin" className="btn primary" data-testid="account-admin-link">Admin panel <ArrowRight size={16} /></Link>}
        <button className="btn outline" data-testid="logout-button" onClick={() => { localStorage.removeItem("ropan-token"); setUser(null); setOrders([]); setMode("login"); setForm({ email: "", password: "", name: "" }); setError(""); }}><LogOut size={16} /> Log out</button>
      </div>
    </div>
    <div className="account-tabs"><b>My orders</b><span>Saved addresses</span><span>Wishlist</span></div>
    {orders.length ? orders.map(o => <div className="order-row" key={o.id}>
      <div><b>{o.id}</b><small>{new Date(o.created_at).toLocaleDateString("en-IN")}</small></div>
      <span className="status-pill">{o.status}</span>
      <strong>{money(o.total)}</strong>
      <ChevronRight size={17} />
    </div>) : <div className="empty compact"><Package size={32} /><h2>No orders yet</h2><Link to="/shop" className="btn primary">Find your first plant</Link></div>}
  </main>;
  return <main className="auth section">
    <div className="auth-visual"><img src={img.hero} alt="Green plants" /><div><span>ROPAN</span><p>Make room for<br /><em>something green.</em></p></div></div>
    <form className="auth-form" onSubmit={submit} data-testid="auth-form">
      <span className="kicker">{mode === "login" ? "WELCOME BACK" : "JOIN THE PLANT FAMILY"}</span>
      <h1>{mode === "login" ? "Sign in to ROPAN" : "Create your account"}</h1>
      {mode !== "login" && <label>Your name<input required data-testid="register-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>}
      <label>Email address<input required type="email" data-testid="auth-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
      <label>Password<input required type="password" data-testid="auth-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></label>
      {error && <div className="form-error" data-testid="auth-error">{error}</div>}
      <button className="btn primary full" data-testid="auth-submit">{mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={16} /></button>
      <p className="switch">{mode === "login" ? "New to ROPAN?" : "Already have an account?"} <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} data-testid="auth-mode-toggle">{mode === "login" ? "Create an account" : "Sign in"}</button></p>
    </form>
  </main>
}

function PlantCare({ settings }) {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", location: "", plants: "", service: "Plant maintenance", date: "", time: "", message: "" });
  const submit = e => { e.preventDefault(); api.post("/bookings", form).then(() => setSent(true)).catch(() => setSent(true)); };
  return <main className="care-page">
    <section className="care-hero">
      <div><span className="kicker">ROPAN PLANT CARE</span><h1>More time enjoying<br /><em>your plants.</em></h1><p>Thoughtful, practical care from people who understand plants.</p>
        <a className="btn dark" href={waLink(settings.whatsapp, "Hello ROPAN, I would like to book a plant care service")} target="_blank" rel="noreferrer" data-testid="plant-care-whatsapp"><MessageCircle size={16} /> Book on WhatsApp</a>
      </div>
      <img src={img.plant} alt="Plant care service" />
    </section>
    <section className="section service-section">
      <div className="section-head"><div><span className="kicker">CARE, YOUR WAY</span><h2>Bring home a little help</h2></div></div>
      <div className="services">{["Plant maintenance", "Cutting & pruning", "Fertilizer application", "Pest & disease treatment", "Plant repotting", "Balcony garden setup"].map((x, i) => <div key={x}><span>0{i + 1}</span><h3>{x}</h3><p>Expert attention, tailored to your plants and your space.</p></div>)}</div>
      <div className="booking">
        <div><span className="kicker">BOOK A VISIT</span><h2>Let's make your<br /><em>garden thrive.</em></h2><p>Tell us a little about what you need. We'll get back to you within one working day.</p></div>
        {sent ? <div className="booking-done"><Sprout /><h3>We're on it.</h3><p>Our plant care team will be in touch soon.</p></div> :
          <form onSubmit={submit} data-testid="plant-care-form">
            {[["name", "Your name"], ["mobile", "Mobile number"], ["location", "Location"], ["plants", "Number of plants"], ["date", "Preferred date"], ["time", "Preferred time"]].map(([k, l]) => <input required key={k} placeholder={l} data-testid={`booking-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} />)}
            <select value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} data-testid="booking-service"><option>Plant maintenance</option><option>Repotting</option><option>Balcony garden setup</option><option>Pest treatment</option></select>
            <textarea placeholder="Anything we should know?" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="booking-message" />
            <button className="btn primary" data-testid="plant-care-submit">Book plant care <ArrowRight size={16} /></button>
          </form>}
      </div>
    </section>
  </main>
}

function About() {
  return <main className="section editorial">
    <span className="kicker">OUR STORY</span>
    <h1>Good plants make<br /><em>good days.</em></h1>
    <p className="editorial-lead">ROPAN is a plant nursery and gardening store for homes, balconies, offices and gardens. We make it easier to bring a little more green into everyday life.</p>
    <img src={img.hero} alt="A lush indoor garden" />
    <div className="editorial-columns">
      <div><h2>Grown with intention</h2><p>We choose healthy plants, quality garden products and simple guidance that helps you feel confident caring for your green companions.</p></div>
      <div><h2>Here for every stage</h2><p>From your first money plant to a full balcony garden, ROPAN brings the right plants and the know-how to help them thrive.</p></div>
    </div>
  </main>
}

function Contact({ settings }) {
  return <main className="section contact">
    <div>
      <span className="kicker">SAY HELLO</span>
      <h1>We'd love to<br /><em>hear from you.</em></h1>
      <p>Questions about a plant, an order or a garden project? Our team is here.</p>
    </div>
    <div className="contact-list">
      <div><MapPin /><span><b>Visit us</b>{settings.address}</span></div>
      <div><Phone /><span><b>Call us</b><a href={`tel:${digits(settings.phone)}`} data-testid="contact-phone">{settings.phone}</a></span></div>
      <div><MessageCircle /><span><b>WhatsApp</b><a href={waLink(settings.whatsapp, "Hello ROPAN")} target="_blank" rel="noreferrer" data-testid="contact-whatsapp">Chat with ROPAN</a></span></div>
      <div><Mail /><span><b>Email</b><a href={`mailto:${settings.email}`} data-testid="contact-email">{settings.email}</a></span></div>
      <div><Truck /><span><b>Hours</b>{settings.business_hours}</span></div>
      <div className="contact-actions">
        <a className="btn primary" href={`mailto:${settings.email}`} data-testid="contact-email-button">Email us <ArrowRight size={16} /></a>
        <a className="btn dark" href={waLink(settings.whatsapp, "Hello ROPAN, I have a question")} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button"><MessageCircle size={16} /> WhatsApp us</a>
      </div>
    </div>
  </main>
}

function Wishlist({ store }) {
  return <main className="section">
    <span className="kicker">SAVED FOR LATER</span>
    <h1>Your wishlist</h1>
    {store.wish.length ? <div className="product-grid">{store.wish.map(p => <ProductCard key={p.id} p={p} store={store} />)}</div> : <div className="empty compact"><Heart size={32} /><h2>No plants saved yet</h2><Link to="/shop" className="btn primary">Discover plants</Link></div>}
  </main>
}

function AdminSettingsForm({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setForm(settings); }, [settings]);
  const token = localStorage.getItem("ropan-token");
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async e => {
    e.preventDefault();
    setSaving(true); setErr(""); setSaved(false);
    try {
      await api.post("/admin/settings", {
        whatsapp: form.whatsapp || "",
        phone: form.phone || "",
        email: form.email || "",
        address: form.address || "",
        business_hours: form.business_hours || "",
        instagram: form.instagram || "",
        facebook: form.facebook || "",
        delivery_charge: Number(form.delivery_charge) || 0,
        free_delivery_minimum: Number(form.free_delivery_minimum) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      onSaved && onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };
  const fields = [
    ["whatsapp", "WhatsApp number", "e.g. 918356098426 (with country code, no +)"],
    ["phone", "Phone number", "e.g. +91 83560 98426"],
    ["email", "Email address", "hello@ropan.in"],
    ["address", "Business address", "Mumbai, Maharashtra, India"],
    ["business_hours", "Business hours", "Mon–Sat, 9am–6pm"],
    ["instagram", "Instagram URL", "https://instagram.com/ropan"],
    ["facebook", "Facebook URL", "https://facebook.com/ropan"],
    ["delivery_charge", "Delivery charge (₹)", "79"],
    ["free_delivery_minimum", "Free delivery over (₹)", "999"],
  ];
  return <form className="settings-form" onSubmit={submit} data-testid="admin-settings-form">
    <div className="settings-grid">
      {fields.map(([k, l, ph]) => <label key={k}>
        <span>{l}</span>
        <input data-testid={`settings-${k}`} value={form[k] ?? ""} onChange={e => change(k, e.target.value)} placeholder={ph} />
      </label>)}
    </div>
    {err && <div className="form-error">{err}</div>}
    <div className="settings-actions">
      <button className="btn primary" disabled={saving} data-testid="settings-save"><Save size={16} /> {saving ? "Saving..." : "Save settings"}</button>
      {saved && <span className="saved-flag" data-testid="settings-saved"><CheckCircle2 size={16} /> Saved</span>}
    </div>
  </form>
}

function ProductManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const token = localStorage.getItem("ropan-token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const backend = process.env.REACT_APP_BACKEND_URL;
  const load = () => { setLoading(true); return api.get("/admin/products", auth).then(r => setProducts(r.data.products)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const startNew = () => { setEditing(null); setShowForm(true); };
  const startEdit = (p) => { setEditing(p); setShowForm(true); };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/products/${p.id}`, auth);
    load();
  };
  const abs = (u) => u && u.startsWith("/api/") ? `${backend}${u}` : u;
  return <div className="pm">
    <div className="panel-head">
      <h2>Products</h2>
      <button className="btn primary small" data-testid="pm-add-product" onClick={startNew}><Plus size={15} /> Add product</button>
    </div>
    <p className="muted">Add, edit or remove products from your live store. Changes appear on the website instantly.</p>
    {showForm && <ProductForm product={editing} onCancel={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
    {loading ? <p className="muted">Loading products...</p> :
      <div className="pm-list" data-testid="pm-product-list">
        {products.map(p => <div className="pm-item" key={p.id} data-testid={`pm-product-${p.id}`}>
          <img src={abs(p.image)} alt={p.name} />
          <div className="pm-body">
            <b>{p.name}</b>
            <small>{p.category}</small>
            <div className="pm-meta"><span>{money(p.price)}</span>{p.compare_price > p.price && <del>{money(p.compare_price)}</del>}<span className={p.stock > 0 ? "in" : "out"}>{p.stock > 0 ? `In stock (${p.stock})` : "Out of stock"}</span></div>
          </div>
          <div className="pm-actions">
            <button className="btn outline small" data-testid={`pm-edit-${p.id}`} onClick={() => startEdit(p)}><Pencil size={14} /> Edit</button>
            <button className="btn danger small" data-testid={`pm-delete-${p.id}`} onClick={() => remove(p)}><Trash2 size={14} /> Delete</button>
          </div>
        </div>)}
        {!products.length && <p className="muted">No products yet. Tap "Add product" to create your first one.</p>}
      </div>}
  </div>
}

function ProductForm({ product, onSaved, onCancel }) {
  const initial = product || { name: "", category: "Indoor Plants", price: "", compare_price: "", description: "", image: "", stock: 10, size: "", light: "Bright indirect light", water: "Water when topsoil feels dry", pet_friendly: false, care: "" };
  const [form, setForm] = useState(initial);
  const [inStock, setInStock] = useState((initial.stock ?? 10) > 0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const token = localStorage.getItem("ropan-token");
  const backend = process.env.REACT_APP_BACKEND_URL;
  const categories = ["Indoor Plants", "Outdoor Plants", "Flowering Plants", "Air Purifying Plants", "Money Plants", "Succulents & Cactus", "Pots & Planters", "Soil & Potting Mix", "Fertilizers", "Gardening Tools", "Gardening Accessories", "Plant Protection"];
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("Image is too large. Please keep it under 8 MB."); return; }
    setErr(""); setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      change("image", r.data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  const submit = async e => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.category.trim() || !form.price) { setErr("Please add product name, category and selling price."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        price: Number(form.price),
        compare_price: Number(form.compare_price) || 0,
        description: form.description || "",
        image: form.image || "",
        stock: inStock ? Math.max(1, Number(form.stock) || 10) : 0,
        size: form.size || "",
        light: form.light || "Bright indirect light",
        water: form.water || "Water when topsoil feels dry",
        pet_friendly: !!form.pet_friendly,
        care: form.care || "",
      };
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      if (product) await api.patch(`/admin/products/${product.id}`, payload, auth);
      else await api.post("/admin/products", payload, auth);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save this product. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const previewSrc = form.image ? (form.image.startsWith("/api/") ? `${backend}${form.image}` : form.image) : "";
  return <form className="pm-form" onSubmit={submit} data-testid="pm-product-form">
    <div className="pm-form-head">
      <h3>{product ? "Edit product" : "Add new product"}</h3>
      <button type="button" className="pm-close" onClick={onCancel}><X size={18} /></button>
    </div>
    <div className="pm-photo">
      {previewSrc ? <img src={previewSrc} alt="preview" data-testid="pm-image-preview" /> : <div className="pm-photo-empty"><ImageIcon size={26} /><span>No photo yet</span></div>}
      <label className="btn outline small pm-upload">
        <Upload size={14} /> {uploading ? "Uploading..." : (form.image ? "Change photo" : "Upload photo")}
        <input type="file" accept="image/*" onChange={e => upload(e.target.files?.[0])} data-testid="pm-photo-input" hidden />
      </label>
    </div>
    <div className="pm-fields">
      <label><span>Product name *</span><input required data-testid="pm-name" value={form.name} onChange={e => change("name", e.target.value)} placeholder="e.g. Money Plant" /></label>
      <label><span>Category *</span>
        <select data-testid="pm-category" value={form.category} onChange={e => change("category", e.target.value)}>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
      </label>
      <label><span>Selling price (₹) *</span><input required type="number" min="1" data-testid="pm-price" value={form.price} onChange={e => change("price", e.target.value)} placeholder="499" /></label>
      <label><span>Original / MRP price (₹)</span><input type="number" min="0" data-testid="pm-compare-price" value={form.compare_price} onChange={e => change("compare_price", e.target.value)} placeholder="699 (optional)" /></label>
      <label className="wide"><span>Short description</span><textarea rows="2" data-testid="pm-description" value={form.description} onChange={e => change("description", e.target.value)} placeholder="Easy-care trailing plant for bright corners" /></label>
      <label><span>Size / Pot</span><input data-testid="pm-size" value={form.size} onChange={e => change("size", e.target.value)} placeholder="6 inch" /></label>
      <label><span>Stock status *</span>
        <div className="pm-stock" data-testid="pm-stock-toggle">
          <button type="button" className={inStock ? "on" : ""} onClick={() => setInStock(true)} data-testid="pm-in-stock">In Stock</button>
          <button type="button" className={!inStock ? "on off" : ""} onClick={() => setInStock(false)} data-testid="pm-out-of-stock">Out of Stock</button>
        </div>
      </label>
      {inStock && <label><span>Units available</span><input type="number" min="1" data-testid="pm-stock-count" value={form.stock} onChange={e => change("stock", e.target.value)} placeholder="10" /></label>}
    </div>
    {err && <div className="form-error" data-testid="pm-error">{err}</div>}
    <div className="pm-form-actions">
      <button type="button" className="btn outline" onClick={onCancel}>Cancel</button>
      <button className="btn primary" disabled={saving || uploading} data-testid="pm-save"><Save size={15} /> {saving ? "Saving..." : (product ? "Save changes" : "Add product")}</button>
    </div>
  </form>
}

function Admin({ settings, reloadSettings }) {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview");
  const [notFound, setNotFound] = useState(false);
  const token = localStorage.getItem("ropan-token");
  const nav = useNavigate();
  const loadOrders = () => api.get("/admin/orders", { headers: { Authorization: `Bearer ${token}` } }).then(r => setOrders(r.data.orders));
  useEffect(() => {
    if (!token) { nav("/account"); return; }
    api.get("/admin/summary", { headers: { Authorization: `Bearer ${token}` } }).then(r => setData(r.data)).catch(e => { if (e.response?.status === 401 || e.response?.status === 403) { setNotFound(true); } });
    loadOrders().catch(() => {});
  }, [token]);
  const updateStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}?status=${encodeURIComponent(status)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    loadOrders();
  };
  if (notFound) return <main className="section"><div className="empty compact"><ShieldCheck size={32} /><h2>Admin access required</h2><Link to="/account" className="btn primary">Sign in as admin</Link></div></main>;
  if (!data) return <main className="loading">Loading dashboard...</main>;
  return <main className="admin section">
    <div className="admin-head">
      <div><span className="kicker">ROPAN CONTROL ROOM</span><h1>Good day, Admin.</h1></div>
      <div className="admin-head-actions">
        <Link to="/" className="btn outline">View store <ArrowRight size={16} /></Link>
        <button className="btn outline" data-testid="admin-logout" onClick={() => { localStorage.removeItem("ropan-token"); nav("/account"); }}><LogOut size={16} /> Log out</button>
      </div>
    </div>
    <div className="admin-tabs">
      <button className={tab === "overview" ? "on" : ""} onClick={() => setTab("overview")} data-testid="admin-tab-overview">Overview</button>
      <button className={tab === "products" ? "on" : ""} onClick={() => setTab("products")} data-testid="admin-tab-products">Products</button>
      <button className={tab === "orders" ? "on" : ""} onClick={() => setTab("orders")} data-testid="admin-tab-orders">Orders</button>
      <button className={tab === "settings" ? "on" : ""} onClick={() => setTab("settings")} data-testid="admin-tab-settings">Business settings</button>
    </div>
    {tab === "overview" && <>
      <div className="metrics">{[["Total sales", money(data.sales), "All time"], ["Orders", data.orders, "This month"], ["Customers", data.customers, "All time"], ["Products", data.products, "In catalogue"]].map(x => <div key={x[0]}><span>{x[0]}</span><strong>{x[1]}</strong><small>{x[2]}</small></div>)}</div>
      <div className="admin-panel">
        <div className="panel-head"><h2>Recent orders</h2><span>{data.pending} active</span></div>
        {orders.slice(0, 8).map(o => <div className="admin-order" key={o.id}>
          <div><b>{o.id}</b><small>{o.customer?.name || "Customer"}</small></div>
          <span className="status-pill">{o.status}</span>
          <strong>{money(o.total)}</strong>
        </div>)}
        {!orders.length && <p className="muted">No orders yet.</p>}
      </div>
    </>}
    {tab === "products" && <div className="admin-panel"><ProductManager /></div>}
    {tab === "orders" && <div className="admin-panel">
      <div className="panel-head"><h2>All orders</h2><span>{orders.length} total</span></div>
      {orders.map(o => <div className="admin-order full" key={o.id}>
        <div><b>{o.id}</b><small>{o.customer?.name} · {o.customer?.mobile}</small><small>{new Date(o.created_at).toLocaleString("en-IN")}</small></div>
        <div className="order-items">{(o.items || []).map(i => <span key={i.id}>{i.name} × {i.quantity}</span>)}</div>
        <strong>{money(o.total)}</strong>
        <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`}>
          {["Order Received", "Confirmed", "Packed", "Shipped", "Out for Delivery", "Delivered", "Cancelled"].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>)}
      {!orders.length && <p className="muted">No orders yet.</p>}
    </div>}
    {tab === "settings" && <div className="admin-panel">
      <div className="panel-head"><h2>Business contact & settings</h2><span>Edit anytime</span></div>
      <p className="muted">These details power the footer, contact page, WhatsApp buttons and delivery calculations across the store.</p>
      <AdminSettingsForm settings={settings} onSaved={reloadSettings} />
    </div>}
  </main>
}

function App() {
  const store = useStore();
  const { settings, reload } = useSettings();
  return <BrowserRouter>
    <Header store={store} />
    <Routes>
      <Route path="/" element={<Home store={store} />} />
      <Route path="/shop" element={<Shop store={store} />} />
      <Route path="/product/:id" element={<Product store={store} settings={settings} />} />
      <Route path="/cart" element={<Cart store={store} settings={settings} />} />
      <Route path="/checkout" element={<Checkout store={store} settings={settings} />} />
      <Route path="/wishlist" element={<Wishlist store={store} />} />
      <Route path="/account" element={<Account />} />
      <Route path="/plant-care" element={<PlantCare settings={settings} />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact settings={settings} />} />
      <Route path="/admin" element={<Admin settings={settings} reloadSettings={reload} />} />
    </Routes>
    <a className="float-wa" href={waLink(settings.whatsapp, "Hello ROPAN")} target="_blank" rel="noreferrer" data-testid="floating-whatsapp"><MessageCircle size={22} /><span>Chat with ROPAN</span></a>
    <Footer settings={settings} />
  </BrowserRouter>
}

export default App;
